/**
 * LayersDecorationRows — the piece-decoration row builders extracted from
 * LayersPanel (#879, epic #828 slice L-S1). LayersPanel OWNS a
 * LayersDecorationRows instance and DRIVES it by composition from `render()`
 * (it is NOT a base class), mirroring the PiecePanelSection /
 * StitchPanelSection precedents: this section holds the per-piece decoration
 * BUILDERS (hole / hardware / stitch / cutout / tracing-image rows, the
 * per-decoration row, and the stitch-line row), while the `render()` dispatch
 * that decides WHEN to call them stays on LayersPanel.
 *
 * Everything the builders read from the owning panel — the list DOM append
 * target, the editor context, the collapsed-set, the chip-visibility flag, the
 * deferred-deselect timer, and the shared row scaffolding (`makeRow` /
 * `makeGroupHeader` free helpers, the click-toggle / visibility-setter /
 * connection-chip / chip-spec / drag-source / path-bounds / stamp-icon builders)
 * — is reached through the `LayersDecorationHost` interface so the moved bodies
 * stay byte-for-byte identical. The shared `makeRow` / `makeGroupHeader` free
 * functions (used by BOTH decoration AND construction rows) stay
 * single-definition in `./LayersPanel` and are imported here. The stitch-line
 * bucketing helpers, used only by these decoration rows, moved down with them.
 * Behaviour is identical to the in-panel original — this is a pure move + host
 * rewiring.
 */
import type { EditorContext } from "../tools/EditorContext";
import {
  isVisible,
  pieceForStitchLine,
  pieceHardware,
  pieceStitchLines,
  projectTracingImages,
  tracingImageBounds,
  type Decoration,
  type Hardware,
  type Hole,
  type Piece,
  type Project,
  type StitchLine,
  type StitchShape,
  type TracingImage,
} from "../model/Project";
import { pickAdjacentDecoration } from "../model/projectOps";
import { IMAGE_ICON_SVG } from "./icons";
import { stitchMatchPick } from "./stitchMatchPick";
import { matchForStitch, matchesForStitch } from "../model/stitchMatch";
import { isFeatureEnabled } from "../featureFlags";
import {
  HARDWARE_COLOR_LABELS,
  HARDWARE_COLORS,
  HARDWARE_TYPE_LABELS,
} from "../hardware/HardwareCatalog";
import type { ConnectionChipSpec } from "../model/connections";
import { targetNoteChipSpecs } from "../model/connections/noteConnection";
import { subTraceChipSpec } from "../model/connections/traceConnection";
import {
  TRIANGLE_DOWN,
  TRIANGLE_RIGHT,
  makeGroupHeader,
  makeRow,
} from "./layersRowKit";

export interface LayersDecorationHost {
  /** The `<ul>`/`<div>` list element every decoration row is appended to. */
  getListEl(): HTMLElement;
  /** The shared editor context (history / commit / scene / selection). */
  getCtx(): EditorContext;
  /** Keys of currently-collapsed containers (shared mutable Set). */
  getCollapsed(): Set<string>;
  /** Whether the per-line constraint chip strips are shown. */
  getChipsVisible(): boolean;
  /** The deferred "toggle deselect" timer (shared with the panel's row
   *  click handling). */
  getDeselectTimer(): ReturnType<typeof setTimeout> | null;
  setDeselectTimer(v: ReturnType<typeof setTimeout> | null): void;
  /** Toggle a container's collapsed state and re-render. */
  toggle(key: string): void;
  /** Register a collapsible container in the current render's tree (#873) so
   *  a parent's expand/collapse-all button can reach it. Decoration sub-groups
   *  pass `headerEl: null` (they only ever nest leaf rows). */
  registerCollapsible(
    key: string,
    parentKey: string | null,
    headerEl: HTMLElement | null,
  ): void;
  /** Inline SVG for a stamp decoration's visibility-cell icon. */
  buildStampIconHtml(stampId: string): string;
  /** Click-toggles-selection / dblclick-zooms factory shared by all rows. */
  buildClickToggle(opts: {
    isSelected: boolean;
    select: () => void;
    deselect: () => void;
    zoomBounds?: () => {
      x: number;
      y: number;
      width: number;
      height: number;
    } | null;
  }): {
    onSelect: () => void;
    onDoubleClick?: () => void;
  };
  /** Bounds of a parsed SVG-path item (for cutout dblclick-zoom). */
  pathDataBounds(pathData: string): {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  setHolesVisibility(pieceId: string, ids: Set<string>, visible: boolean): void;
  setHardwareVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ): void;
  setStitchVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ): void;
  setCutoutsVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ): void;
  /** Build one connection/constraint chip element. */
  buildConnectionChip(spec: ConnectionChipSpec): HTMLSpanElement;
  /** Chip spec marking a seam's place in the construction-stitch grid. */
  stitchGridChipSpec(line: StitchLine): ConnectionChipSpec | null;
  /** Chip spec for a stitch-match relationship. */
  stitchMatchChipSpec(
    match: ReturnType<typeof matchForStitch> & object,
    stitchId: string,
  ): ConnectionChipSpec;
  /** Make a construction/tracing row draggable for reordering. */
  installDragSource(
    el: HTMLElement,
    source: { kind: "line" | "group" | "image"; id: string },
  ): void;
}

export class LayersDecorationRows {
  constructor(private host: LayersDecorationHost) {}

  // — Injected panel services, reached through the host so the moved builder
  //   bodies stay byte-for-byte identical (`this.ctx` / `this.listEl` / … keep
  //   working). The real implementations stay single-definition on the panel. —
  private get ctx(): EditorContext {
    return this.host.getCtx();
  }
  private get listEl(): HTMLElement {
    return this.host.getListEl();
  }
  private get collapsed(): Set<string> {
    return this.host.getCollapsed();
  }
  private get chipsVisible(): boolean {
    return this.host.getChipsVisible();
  }
  private get deselectTimer(): ReturnType<typeof setTimeout> | null {
    return this.host.getDeselectTimer();
  }
  private set deselectTimer(v: ReturnType<typeof setTimeout> | null) {
    this.host.setDeselectTimer(v);
  }
  private toggle(key: string): void {
    this.host.toggle(key);
  }
  private registerCollapsible(
    key: string,
    parentKey: string | null,
    headerEl: HTMLElement | null,
  ): void {
    this.host.registerCollapsible(key, parentKey, headerEl);
  }
  private buildStampIconHtml(stampId: string): string {
    return this.host.buildStampIconHtml(stampId);
  }
  private buildClickToggle(
    opts: Parameters<LayersDecorationHost["buildClickToggle"]>[0],
  ): ReturnType<LayersDecorationHost["buildClickToggle"]> {
    return this.host.buildClickToggle(opts);
  }
  private pathDataBounds(
    pathData: string,
  ): ReturnType<LayersDecorationHost["pathDataBounds"]> {
    return this.host.pathDataBounds(pathData);
  }
  private setHolesVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ): void {
    this.host.setHolesVisibility(pieceId, ids, visible);
  }
  private setHardwareVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ): void {
    this.host.setHardwareVisibility(pieceId, ids, visible);
  }
  private setStitchVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ): void {
    this.host.setStitchVisibility(pieceId, ids, visible);
  }
  private setCutoutsVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ): void {
    this.host.setCutoutsVisibility(pieceId, ids, visible);
  }
  private buildConnectionChip(spec: ConnectionChipSpec): HTMLSpanElement {
    return this.host.buildConnectionChip(spec);
  }
  private stitchGridChipSpec(line: StitchLine): ConnectionChipSpec | null {
    return this.host.stitchGridChipSpec(line);
  }
  private stitchMatchChipSpec(
    match: ReturnType<typeof matchForStitch> & object,
    stitchId: string,
  ): ConnectionChipSpec {
    return this.host.stitchMatchChipSpec(match, stitchId);
  }
  private installDragSource(
    el: HTMLElement,
    source: { kind: "line" | "group" | "image"; id: string },
  ): void {
    this.host.installDragSource(el, source);
  }

  public buildDecorationRow(
    piece: Piece,
    dec: Decoration,
    sel: { pieceId: string | null; decorationId: string | null },
    indentPx: number,
  ): HTMLDivElement {
    const isStampKind =
      dec.kind === "stamp" || dec.kind === "lineStamp" || dec.kind === "fill";
    const stampIconHtml = isStampKind
      ? this.buildStampIconHtml(dec.stampId)
      : "";
    return makeRow({
      leadingGlyph: stampIconHtml ? "" : dec.visible ? "●" : "○",
      leadingHtml: stampIconHtml || undefined,
      hidden: !dec.visible,
      label: dec.name,
      selected: sel.pieceId === piece.id && sel.decorationId === dec.id,
      indentPx,
      onLeadingClick: () => {
        this.ctx.commit("Toggle visibility", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((pc) =>
            pc.id === piece.id
              ? {
                  ...pc,
                  decorations: pc.decorations.map((d) =>
                    d.id === dec.id ? { ...d, visible: !d.visible } : d,
                  ),
                }
              : pc,
          ),
        }));
      },
      onSelect: () =>
        this.ctx.setSelection({ pieceId: piece.id, decorationId: dec.id }),
      onDelete: () => {
        const currentPiece = this.ctx.history.state.pieces.find(
          (p) => p.id === piece.id,
        );
        const nextDecorationId = currentPiece
          ? pickAdjacentDecoration(currentPiece, dec.id)
          : null;
        this.ctx.commit("Delete decoration", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((pc) =>
            pc.id === piece.id
              ? {
                  ...pc,
                  decorations: pc.decorations.filter((d) => d.id !== dec.id),
                }
              : pc,
          ),
        }));
        // Always move selection to the next sibling (or fall back to the
        // parent part) — even if the deleted row wasn't the current
        // selection, this leaves the keyboard in a sensible state.
        this.ctx.setSelection({
          pieceId: piece.id,
          decorationId: nextDecorationId,
        });
      },
    });
  }

  public appendHoleRows(
    piece: Piece,
    sel: { pieceId: string | null; holeId?: string | null },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    if (piece.holes.length === 0) return;

    // Bucket holes by rounded radius so a part with mixed sizes shows a
    // group per size instead of one big "Holes" pile.
    interface HoleBucket {
      radius: number;
      key: string;
      entries: { hole: Hole; originalIndex: number }[];
    }
    const buckets = new Map<string, HoleBucket>();
    const orderedKeys: string[] = [];
    piece.holes.forEach((hole, originalIndex) => {
      const key = hole.radius.toFixed(2);
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = { radius: hole.radius, key, entries: [] };
        buckets.set(key, bucket);
        orderedKeys.push(key);
      }
      bucket.entries.push({ hole, originalIndex });
    });

    for (const key of orderedKeys) {
      const bucket = buckets.get(key)!;
      const showGroup = bucket.entries.length > 1;
      const groupKey = `holes:${piece.id}:${key}`;
      const open = !this.collapsed.has(groupKey);

      const diameter = bucket.radius * 2;
      if (showGroup) {
        const bucketHoleIds = new Set(bucket.entries.map((e) => e.hole.id));
        const groupAnyVisible = bucket.entries.some((e) => isVisible(e.hole));
        const header = makeGroupHeader({
          toggleGlyph: open ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
          onToggle: () => this.toggle(groupKey),
          leadingHtml: "◯",
          hidden: !groupAnyVisible,
          onLeadingClick: () =>
            this.setHolesVisibility(piece.id, bucketHoleIds, !groupAnyVisible),
          label: `Holes ⌀${diameter.toFixed(2)} mm (${bucket.entries.length})`,
          indentPx: 16,
          onDelete: () => {
            this.ctx.commit("Delete hole group", (proj) => ({
              ...proj,
              pieces: proj.pieces.map((pc) =>
                pc.id === piece.id
                  ? {
                      ...pc,
                      holes: pc.holes.filter((h) => !bucketHoleIds.has(h.id)),
                    }
                  : pc,
              ),
            }));
            if (
              sel.pieceId === piece.id &&
              sel.holeId &&
              bucketHoleIds.has(sel.holeId)
            ) {
              this.ctx.setSelection({
                pieceId: piece.id,
                decorationId: null,
                holeId: null,
              });
            }
            this.collapsed.delete(groupKey);
          },
        });
        this.listEl.appendChild(header);
        this.registerCollapsible(groupKey, `part:${piece.id}`, null);
        if (!open) continue;
      }

      const indent = showGroup ? 36 : 16;
      for (const { hole, originalIndex } of bucket.entries) {
        const matches = sel.pieceId === piece.id && sel.holeId === hole.id;
        const holeDiameter = hole.radius * 2;
        const label = `Hole ${originalIndex + 1} (⌀${holeDiameter.toFixed(2)} mm)`;
        const visible = isVisible(hole);
        const click = this.buildClickToggle({
          isSelected: matches,
          select: () =>
            this.ctx.setSelection({
              pieceId: piece.id,
              decorationId: null,
              holeId: hole.id,
            }),
          deselect: () =>
            this.ctx.setSelection({
              pieceId: piece.id,
              decorationId: null,
              holeId: null,
            }),
          // Hole bounds = circle bbox at the hole's center.
          zoomBounds: () => ({
            x: hole.position.x - hole.radius,
            y: hole.position.y - hole.radius,
            width: hole.radius * 2,
            height: hole.radius * 2,
          }),
        });
        const row = makeRow({
          leadingGlyph: "◯",
          hidden: !visible,
          onLeadingClick: () =>
            this.setHolesVisibility(piece.id, new Set([hole.id]), !visible),
          label,
          selected: matches,
          indentPx: indent,
          onSelect: click.onSelect,
          onDoubleClick: click.onDoubleClick,
          onDelete: () => {
            const next =
              piece.holes[originalIndex + 1]?.id ??
              piece.holes[originalIndex - 1]?.id ??
              null;
            this.ctx.commit("Delete hole", (proj) => ({
              ...proj,
              pieces: proj.pieces.map((pc) =>
                pc.id === piece.id
                  ? { ...pc, holes: pc.holes.filter((h) => h.id !== hole.id) }
                  : pc,
              ),
            }));
            this.ctx.setSelection({
              pieceId: piece.id,
              decorationId: null,
              holeId: next,
            });
          },
        });
        this.listEl.appendChild(row);
        track({ element: row, matchesSelection: matches });
      }
    }
  }

  /** Hardware list for one piece, bucketed by `${type}|${color}` so a part
   *  with a mix of brass rivets, silver snaps, and antiqued conchos shows
   *  one collapsible group per (type, colour) pair instead of a flat
   *  pile. Within a bucket, snap / magnetic-snap entries also show their
   *  side (top / bottom) since that's the meaningful per-row distinction
   *  the user thinks in. Selection / deletion / visibility follow the
   *  same conventions as the holes panel. */
  public appendHardwareRows(
    piece: Piece,
    sel: { pieceId: string | null },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    const list = pieceHardware(piece);
    if (list.length === 0) return;

    interface HwBucket {
      key: string;
      type: Hardware["type"];
      color: Hardware["color"];
      entries: Hardware[];
    }
    const buckets = new Map<string, HwBucket>();
    const orderedKeys: string[] = [];
    for (const hw of list) {
      const key = `${hw.type}|${hw.color}`;
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = { key, type: hw.type, color: hw.color, entries: [] };
        buckets.set(key, bucket);
        orderedKeys.push(key);
      }
      bucket.entries.push(hw);
    }

    for (const key of orderedKeys) {
      const bucket = buckets.get(key)!;
      const groupKey = `hardware:${piece.id}:${key}`;
      const open = !this.collapsed.has(groupKey);
      const showGroup = bucket.entries.length > 1;
      const colorHex = HARDWARE_COLORS[bucket.color];
      // Inline coloured swatch — matches the canvas render colour so the
      // user can tell at a glance which bucket is which without reading.
      const leadingHtml = `<span class="layers-hardware-swatch" style="background:${colorHex}"></span>`;
      const groupLabel = `${HARDWARE_COLOR_LABELS[bucket.color]} ${HARDWARE_TYPE_LABELS[bucket.type].toLowerCase()} (${bucket.entries.length})`;

      if (showGroup) {
        const bucketIds = new Set(bucket.entries.map((h) => h.id));
        const groupAnyVisible = bucket.entries.some((h) => isVisible(h));
        const header = makeGroupHeader({
          toggleGlyph: open ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
          onToggle: () => this.toggle(groupKey),
          leadingHtml,
          hidden: !groupAnyVisible,
          onLeadingClick: () =>
            this.setHardwareVisibility(piece.id, bucketIds, !groupAnyVisible),
          label: groupLabel,
          indentPx: 16,
          onDelete: () => {
            this.ctx.commit("Delete hardware group", (proj) => ({
              ...proj,
              pieces: proj.pieces.map((pc) =>
                pc.id === piece.id
                  ? {
                      ...pc,
                      hardware: pieceHardware(pc).filter(
                        (h) => !bucketIds.has(h.id),
                      ),
                    }
                  : pc,
              ),
            }));
            this.collapsed.delete(groupKey);
          },
        });
        this.listEl.appendChild(header);
        this.registerCollapsible(groupKey, `part:${piece.id}`, null);
        if (!open) continue;
      }

      const indent = showGroup ? 36 : 16;
      for (const hw of bucket.entries) {
        const visible = isVisible(hw);
        // Suffix label with side for snaps / magnetic snaps so the row tells
        // the two halves apart inside a single-colour bucket.
        const sideLabel =
          hw.type === "snap" || hw.type === "magnetic-snap"
            ? hw.side === "male"
              ? " — male"
              : " — female"
            : "";
        const rowLabel = `${HARDWARE_TYPE_LABELS[hw.type]}${sideLabel} (⌀${hw.sizeMm.toFixed(1)} mm)`;
        const matches = false; // hardware selection isn't wired through `sel` yet
        const hole = piece.holes.find((h) => h.id === hw.holeId);
        const row = makeRow({
          // `leadingGlyph` is required by RowOptions; empty string lets the
          // `leadingHtml` swatch take over the slot the way decoration rows do.
          leadingGlyph: "",
          leadingHtml,
          hidden: !visible,
          onLeadingClick: () =>
            this.setHardwareVisibility(piece.id, new Set([hw.id]), !visible),
          label: rowLabel,
          selected: matches,
          indentPx: indent,
          onSelect: () => {
            // Selecting hardware via the layers panel isn't routed through
            // `setSelection` yet (the canvas hit-test picks it up but the
            // properties panel doesn't show a per-item editor). Treat the
            // click as a no-op for now — visibility / delete still work.
          },
          // Double-click zooms to the hardware's cap regardless of
          // whether selection is wired up — useful for finding small
          // hardware items by name in a busy project.
          onDoubleClick: hole
            ? () => {
                const r = hw.sizeMm / 2;
                this.ctx.viewport.fitToBounds({
                  x: hole.position.x - r,
                  y: hole.position.y - r,
                  width: r * 2,
                  height: r * 2,
                });
              }
            : undefined,
          onDelete: () => {
            this.ctx.commit("Delete hardware", (proj) => ({
              ...proj,
              pieces: proj.pieces.map((pc) =>
                pc.id === piece.id
                  ? {
                      ...pc,
                      hardware: pieceHardware(pc).filter((h) => h.id !== hw.id),
                    }
                  : pc,
              ),
            }));
          },
        });
        this.listEl.appendChild(row);
        track({ element: row, matchesSelection: matches });
      }
    }
  }

  public appendStitchRows(
    piece: Piece,
    sel: ReturnType<typeof this.ctx.scene.getSelection>,
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    const lines = pieceStitchLines(piece);
    if (lines.length === 0) return;

    // Bucket the lines by override key. Each bucket renders as its own
    // top-level "Stitching" entry — siblings, not nested. The inheriting
    // bucket gets the bare label; override buckets get a qualifier like
    // "Stitching (slant)" so the user can tell them apart at a glance.
    const buckets = bucketStitchLines(lines);
    for (const bucket of buckets) {
      const bucketKey = `stitch-bucket:${piece.id}:${bucket.key}`;
      const bucketOpen = !this.collapsed.has(bucketKey);
      const bucketIds = new Set(bucket.items.map((it) => it.line.id));
      const anyVisible = bucket.items.some((it) => isVisible(it.line));
      const isInheritBucket = bucket.key === "inherits";
      const label = isInheritBucket
        ? `Stitching (${bucket.items.length})`
        : `Stitching (${bucket.label}) (${bucket.items.length})`;
      const isSelected = isBucketSelected(
        sel,
        piece.id,
        bucketIds,
        isInheritBucket,
      );
      const header = makeGroupHeader({
        toggleGlyph: bucketOpen ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
        onToggle: () => this.toggle(bucketKey),
        leadingHtml: "⫶",
        hidden: !anyVisible,
        selected: isSelected,
        onLeadingClick: () =>
          this.setStitchVisibility(piece.id, bucketIds, !anyVisible),
        // Inherit bucket points to the part scope (where editing the part
        // defaults flows through). Override buckets get their own
        // bucket-scope so the editor can mutate just that subset of lines.
        onSelect: () =>
          this.ctx.setSelection({
            pieceId: null,
            decorationId: null,
            stitchingScope: isInheritBucket
              ? { kind: "piece", pieceId: piece.id }
              : {
                  kind: "bucket",
                  pieceId: piece.id,
                  lineIds: bucket.items.map((it) => it.line.id),
                },
          }),
        label,
        indentPx: 16,
        onDelete: () => {
          this.ctx.commit("Delete stitch bucket", (proj) => ({
            ...proj,
            pieces: proj.pieces.map((pc) =>
              pc.id === piece.id
                ? {
                    ...pc,
                    stitchLines: pieceStitchLines(pc).filter(
                      (l) => !bucketIds.has(l.id),
                    ),
                  }
                : pc,
            ),
          }));
          if (sel.stitchId && bucketIds.has(sel.stitchId)) {
            this.ctx.setSelection({
              pieceId: piece.id,
              decorationId: null,
              stitchId: null,
            });
          }
          this.collapsed.delete(bucketKey);
        },
      });
      this.listEl.appendChild(header);
      this.registerCollapsible(bucketKey, `part:${piece.id}`, null);
      if (!bucketOpen) continue;
      for (const item of bucket.items) {
        this.appendStitchLineRow(
          piece,
          item.line,
          item.originalIndex,
          36,
          sel,
          track,
        );
      }
    }
  }

  private appendStitchLineRow(
    piece: Piece,
    line: StitchLine,
    originalIndex: number,
    indentPx: number,
    sel: ReturnType<typeof this.ctx.scene.getSelection>,
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    // Multi-select set for "Match selected" (#199 path B). Stitch ids are
    // globally unique, so the set spans pieces; the row highlights when it's a
    // member, not just the single lead.
    const selectedStitchIds =
      sel.stitchIds ?? (sel.stitchId ? [sel.stitchId] : []);
    const inMulti = selectedStitchIds.includes(line.id);
    const matches =
      inMulti || (sel.pieceId === piece.id && sel.stitchId === line.id);
    const visible = isVisible(line);
    const ref = { pieceId: piece.id, stitchId: line.id };
    const leadPieceFor = (id: string | null) =>
      id
        ? (pieceForStitchLine(this.ctx.history.state, id)?.id ?? piece.id)
        : null;
    // Reused only for its dblclick zoom-to-fit; selection is handled inline
    // below so the row can read the click's modifier keys for multi-select.
    const click = this.buildClickToggle({
      isSelected: matches,
      select: () => {},
      deselect: () => {},
      // Stitch-line bounds = bbox of its polyline vertices.
      zoomBounds: () => {
        if (line.points.length === 0) return null;
        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        for (const p of line.points) {
          if (p.x < minX) minX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.x > maxX) maxX = p.x;
          if (p.y > maxY) maxY = p.y;
        }
        return {
          x: minX,
          y: minY,
          width: Math.max(0.001, maxX - minX),
          height: Math.max(0.001, maxY - minY),
        };
      },
    });
    // Stitch-match chip (#199): a stitching-icon chip marking the seam's match,
    // sharing the connection key with the Properties Connections row so hovering
    // either side cross-highlights the other.
    let matchTrailing: HTMLElement | undefined;
    if (this.chipsVisible) {
      const chips: ConnectionChipSpec[] = [];
      // Trace chip (#1202): a seam traced from construction lines — or promoted
      // with Create construction line — shows the link, as a traced part does.
      const traceChip = subTraceChipSpec(this.ctx.history.state, {
        kind: "stitch",
        pieceId: piece.id,
        id: line.id,
      });
      if (traceChip) chips.push(traceChip);
      if (isFeatureEnabled("stitch-matching")) {
        // A main can have several followers (#199 path B) — one chip per match.
        const matches = matchesForStitch(this.ctx.history.state, line.id);
        for (const match of matches) {
          chips.push(this.stitchMatchChipSpec(match, line.id));
        }
        // Construction-stitch grid (#199 Phase 3): one chip flagging whether
        // this seam LEADS the shared grid or follows it — sharing the connection
        // key so it cross-highlights the Properties grid row + the group's other
        // chips.
        const gridChip = this.stitchGridChipSpec(line);
        if (gridChip) chips.push(gridChip);
      }
      // Plus one chip per note pointing at THIS seam (#1076) — the reverse of
      // the chip on the note's own row, sharing its key so hovering either
      // lights up both. Outside the `stitch-matching` gate: writing a note about
      // a seam has nothing to do with matching two of them.
      chips.push(
        ...targetNoteChipSpecs(this.ctx.history.state, {
          pieceId: piece.id,
          stitchId: line.id,
        }),
      );
      if (chips.length > 0) {
        const strip = document.createElement("span");
        strip.className = "constraint-chip-strip";
        for (const spec of chips) {
          strip.appendChild(this.buildConnectionChip(spec));
        }
        matchTrailing = strip;
      }
    }

    const row = makeRow({
      leadingGlyph: "⫶",
      hidden: !visible,
      trailing: matchTrailing,
      onLeadingClick: () =>
        this.setStitchVisibility(piece.id, new Set([line.id]), !visible),
      label: line.name ?? `Stitch ${originalIndex + 1}`,
      selected: matches,
      indentPx,
      onSelect: (ev) => {
        // While a "Match to another seam" pick is armed (#199), selecting a
        // seam in the parts list completes the match — tap-only, so it works on
        // a keyboard-less device (iPad). This is the touch-friendly path to a
        // match; "Match selected" below needs the modifier multi-select.
        if (stitchMatchPick.isArmed()) {
          stitchMatchPick.complete(this.ctx, ref);
          return;
        }
        if (this.deselectTimer) {
          clearTimeout(this.deselectTimer);
          this.deselectTimer = null;
        }
        // Modifier semantics mirror the canvas StitchTool / SelectTool and the
        // part rows: Shift = additive, Cmd/Ctrl = toggle, plain = single-select.
        const additive = ev.shiftKey;
        const toggle = ev.ctrlKey || ev.metaKey;
        if (toggle) {
          const next = inMulti
            ? selectedStitchIds.filter((id) => id !== line.id)
            : [...selectedStitchIds, line.id];
          const lead = next[next.length - 1] ?? null;
          this.ctx.setSelection({
            pieceId: leadPieceFor(lead),
            decorationId: null,
            stitchId: lead,
            stitchIds: next,
          });
          return;
        }
        if (additive) {
          if (inMulti) return;
          const next = [...selectedStitchIds, line.id];
          this.ctx.setSelection({
            pieceId: piece.id,
            decorationId: null,
            stitchId: line.id,
            stitchIds: next,
          });
          return;
        }
        // Plain click: single-select; a reclick on a sole selection clears it
        // (deferred so a double-click can zoom-to-fit first).
        if (matches && selectedStitchIds.length === 1) {
          this.deselectTimer = setTimeout(() => {
            this.deselectTimer = null;
            this.ctx.setSelection({
              pieceId: piece.id,
              decorationId: null,
              stitchId: null,
            });
          }, 250);
        } else {
          this.ctx.setSelection({
            pieceId: piece.id,
            decorationId: null,
            stitchId: line.id,
          });
        }
      },
      onDoubleClick: click.onDoubleClick,
      onDelete: () => {
        const all = pieceStitchLines(piece);
        const next =
          all[originalIndex + 1]?.id ?? all[originalIndex - 1]?.id ?? null;
        this.ctx.commit("Delete stitch line", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((pc) =>
            pc.id === piece.id
              ? {
                  ...pc,
                  stitchLines: pieceStitchLines(pc).filter(
                    (l) => l.id !== line.id,
                  ),
                }
              : pc,
          ),
        }));
        this.ctx.setSelection({
          pieceId: piece.id,
          decorationId: null,
          stitchId: next,
        });
      },
    });
    // Affordance while a match pick is armed (#199): candidate seams highlight
    // as pickable; the armed (follower-to-be) seam marks itself.
    if (stitchMatchPick.isArmed()) {
      if (stitchMatchPick.isSource(ref)) {
        row.classList.add("layers-row--match-source");
        row.title = "Pick a seam for this one to follow";
      } else {
        row.classList.add("layers-row--match-target");
        row.title = "Make this the seam the armed one follows";
      }
    }
    this.listEl.appendChild(row);
    track({ element: row, matchesSelection: matches });
  }

  /** Renders one tracing-image leaf row: visibility dot, name, delete.
   *  Plain click single-selects (opening the Properties panel, issue
   *  #498); Cmd/Ctrl/Shift-click toggles it into the multi-select set
   *  alongside any selected lines / groups so images and lines can be
   *  grouped together (issue #509). The row is a drag source for
   *  re-parenting into / out of construction groups. Shared by the
   *  top-level pass and the in-group nesting pass. */
  public appendTracingImageRow(
    image: TracingImage,
    indentPx: number,
    sel: {
      constructionId?: string | null;
      constructionIds?: string[];
      constructionGroupIds?: string[];
      tracingImageIds?: string[];
    },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    const visible = isVisible(image);
    const selectedImageIds = sel.tracingImageIds ?? [];
    const selected = selectedImageIds.includes(image.id);
    const row = makeRow({
      // Use the same photo icon the "add reference image" toolbar button
      // shows (#545), so an image reads the same in the toolbar and the
      // layer list. The leading cell still toggles visibility on click;
      // the hidden state shows via the row fade (same as stamp-icon rows).
      leadingGlyph: "",
      leadingHtml: IMAGE_ICON_SVG,
      hidden: !visible,
      indentPx,
      onLeadingClick: () => {
        this.ctx.commit(
          visible ? "Hide tracing image" : "Show tracing image",
          (proj) => ({
            ...proj,
            tracingImages: projectTracingImages(proj).map((img) =>
              img.id === image.id ? { ...img, visible: !visible } : img,
            ),
          }),
        );
      },
      label: image.name,
      selected,
      onSelect: (ev: MouseEvent) => {
        const isToggle = ev.metaKey || ev.ctrlKey || ev.shiftKey;
        if (isToggle) {
          // Add / toggle into the multi-select set, preserving any
          // selected construction lines / groups so a mixed selection
          // can be grouped with Cmd+G.
          const isIn = selectedImageIds.includes(image.id);
          const nextImages = isIn
            ? selectedImageIds.filter((id) => id !== image.id)
            : [...selectedImageIds, image.id];
          this.ctx.setSelection({
            pieceId: null,
            decorationId: null,
            constructionIds: sel.constructionIds ?? [],
            constructionGroupIds: sel.constructionGroupIds ?? [],
            tracingImageIds: nextImages,
          });
          return;
        }
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          tracingImageId: image.id,
        });
      },
      onDelete: () => {
        this.ctx.commit("Delete tracing image", (proj) => ({
          ...proj,
          tracingImages: projectTracingImages(proj).filter(
            (img) => img.id !== image.id,
          ),
        }));
      },
      // Double-click zooms to fit the image, the same affordance
      // construction lines / groups / parts already have (#545). Uses the
      // rotated AABB so a turned image still frames correctly.
      onDoubleClick: () => {
        const b = tracingImageBounds(image);
        if (b.width > 0 && b.height > 0) this.ctx.viewport.fitToBounds(b);
      },
      dataAttributes: { "data-tracing-image-id": image.id },
    });
    this.installDragSource(row, { kind: "image", id: image.id });
    if (selected) track({ element: row, matchesSelection: true });
    this.listEl.appendChild(row);
  }

  public appendCutoutRows(
    piece: Piece,
    sel: { pieceId: string | null; cutoutId?: string | null },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    if (piece.cutouts.length === 0) return;
    const groupKey = `cutouts:${piece.id}`;
    const open = !this.collapsed.has(groupKey);
    if (piece.cutouts.length > 1) {
      const cutoutIds = new Set(piece.cutouts.map((c) => c.id));
      const groupAnyVisible = piece.cutouts.some((c) => isVisible(c));
      const header = makeGroupHeader({
        toggleGlyph: open ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
        onToggle: () => this.toggle(groupKey),
        leadingHtml: "✂",
        hidden: !groupAnyVisible,
        onLeadingClick: () =>
          this.setCutoutsVisibility(piece.id, cutoutIds, !groupAnyVisible),
        label: `Cutouts (${piece.cutouts.length})`,
        indentPx: 16,
        onDelete: () => {
          this.ctx.commit("Delete cutout group", (proj) => ({
            ...proj,
            pieces: proj.pieces.map((pc) =>
              pc.id === piece.id
                ? {
                    ...pc,
                    cutouts: pc.cutouts.filter((c) => !cutoutIds.has(c.id)),
                  }
                : pc,
            ),
          }));
          if (
            sel.pieceId === piece.id &&
            sel.cutoutId &&
            cutoutIds.has(sel.cutoutId)
          ) {
            this.ctx.setSelection({
              pieceId: piece.id,
              decorationId: null,
              cutoutId: null,
            });
          }
          this.collapsed.delete(groupKey);
        },
      });
      this.listEl.appendChild(header);
      this.registerCollapsible(groupKey, `part:${piece.id}`, null);
      if (!open) return;
    }
    const indent = piece.cutouts.length > 1 ? 36 : 16;
    piece.cutouts.forEach((cut, idx) => {
      const matches = sel.pieceId === piece.id && sel.cutoutId === cut.id;
      const visible = isVisible(cut);
      const click = this.buildClickToggle({
        isSelected: matches,
        select: () =>
          this.ctx.setSelection({
            pieceId: piece.id,
            decorationId: null,
            cutoutId: cut.id,
          }),
        deselect: () =>
          this.ctx.setSelection({
            pieceId: piece.id,
            decorationId: null,
            cutoutId: null,
          }),
        zoomBounds: () => this.pathDataBounds(cut.pathData),
      });
      // One chip per note pointing at THIS cutout (#1076) — a note about a
      // window in the leather belongs on the window's row, not on the part's.
      let trailing: HTMLElement | undefined;
      if (this.chipsVisible) {
        // Trace chip first (#1202): a traced cutout shows its link like a
        // traced part does.
        const traceChip = subTraceChipSpec(this.ctx.history.state, {
          kind: "cutout",
          pieceId: piece.id,
          id: cut.id,
        });
        const specs = [
          ...(traceChip ? [traceChip] : []),
          ...targetNoteChipSpecs(this.ctx.history.state, {
            pieceId: piece.id,
            cutoutId: cut.id,
          }),
        ];
        if (specs.length > 0) {
          trailing = document.createElement("span");
          trailing.className = "constraint-chip-strip";
          for (const spec of specs) {
            trailing.appendChild(this.buildConnectionChip(spec));
          }
        }
      }
      const row = makeRow({
        leadingGlyph: "✂",
        hidden: !visible,
        trailing,
        onLeadingClick: () =>
          this.setCutoutsVisibility(piece.id, new Set([cut.id]), !visible),
        label: cut.name ?? `Cutout ${idx + 1}`,
        selected: matches,
        indentPx: indent,
        onSelect: click.onSelect,
        onDoubleClick: click.onDoubleClick,
        onDelete: () => {
          const next =
            piece.cutouts[idx + 1]?.id ?? piece.cutouts[idx - 1]?.id ?? null;
          this.ctx.commit("Delete cutout", (proj) => ({
            ...proj,
            pieces: proj.pieces.map((pc) =>
              pc.id === piece.id
                ? { ...pc, cutouts: pc.cutouts.filter((c) => c.id !== cut.id) }
                : pc,
            ),
          }));
          this.ctx.setSelection({
            pieceId: piece.id,
            decorationId: null,
            cutoutId: next,
          });
        },
      });
      this.listEl.appendChild(row);
      track({ element: row, matchesSelection: matches });
    });
  }
}

interface StitchLineBucketItem {
  line: StitchLine;
  /** Index within `piece.stitchLines` — preserved so onDelete / "Stitch N"
   *  fallbacks line up with the underlying array. */
  originalIndex: number;
}

interface StitchLineBucket {
  /** Stable key derived from the override settings. Used for collapse-state
   *  tracking. */
  key: string;
  label: string;
  items: StitchLineBucketItem[];
}

/** Group a part's stitch lines by their override key — non-overriding lines
 *  share a single "Defaults" bucket, and each distinct override produces a
 *  separate bucket. The inheriting bucket is always returned first (so the
 *  panel reads "defaults, then deviations"); override buckets follow in
 *  first-occurrence order. */
function bucketStitchLines(lines: StitchLine[]): StitchLineBucket[] {
  const map = new Map<string, StitchLineBucket>();
  const overrideOrder: StitchLineBucket[] = [];
  let inheritBucket: StitchLineBucket | null = null;
  lines.forEach((line, originalIndex) => {
    const isInherit = line.overridesDefaults === false;
    // Tight numeric formatting in the key so trivial floating-point fuzz
    // doesn't split otherwise-identical buckets.
    const key = isInherit
      ? "inherits"
      : `over:${line.shape}:${line.pitch.toFixed(3)}:${line.size.toFixed(3)}:${line.slantAngle.toFixed(1)}`;
    let bucket = map.get(key);
    if (!bucket) {
      bucket = {
        key,
        label: isInherit ? "Defaults" : stitchOverrideLabel(line),
        items: [],
      };
      map.set(key, bucket);
      if (isInherit) inheritBucket = bucket;
      else overrideOrder.push(bucket);
    }
    bucket.items.push({ line, originalIndex });
  });
  return inheritBucket ? [inheritBucket, ...overrideOrder] : overrideOrder;
}

function stitchOverrideLabel(line: StitchLine): string {
  const shape: StitchShape = line.shape;
  return shape === "round"
    ? "round"
    : shape === "diamond"
      ? "diamond"
      : "slant";
}

/** Decide whether a bucket header should render selected, given the active
 *  selection. Inherit-bucket selection arrives as a piece-scope; override
 *  buckets are matched by line-id set equality so the selection survives an
 *  edit that changes the serialized bucket key. */
function isBucketSelected(
  sel: {
    stitchingScope?: {
      kind: string;
      pieceId?: string;
      lineIds?: string[];
    } | null;
  },
  pieceId: string,
  bucketIds: Set<string>,
  isInheritBucket: boolean,
): boolean {
  const sc = sel.stitchingScope;
  if (!sc) return false;
  if (isInheritBucket) {
    return sc.kind === "piece" && sc.pieceId === pieceId;
  }
  if (sc.kind !== "bucket") return false;
  if (sc.pieceId !== pieceId) return false;
  if (!sc.lineIds || sc.lineIds.length !== bucketIds.size) return false;
  for (const id of sc.lineIds) {
    if (!bucketIds.has(id)) return false;
  }
  return true;
}
